import type { Pass } from '@/types';
import {
  compareCalendarDates,
  formatDateForInput,
  getCalendarDayDifference,
  parseCalendarDateInput,
} from '@/shared/lib/calendarDate';

export type PassFormValues = {
  name: string;
  startDate: string;
  endDate: string;
  remainingCredits: string;
  totalCredits: string;
  isUnlimitedDuration: boolean;
  isUnlimitedCredits: boolean;
};

export const emptyPassFormValues: PassFormValues = {
  name: '',
  startDate: '',
  endDate: '',
  remainingCredits: '',
  totalCredits: '',
  isUnlimitedDuration: false,
  isUnlimitedCredits: false,
};

export const passToFormValues = (pass?: Pass | null): PassFormValues => {
  if (!pass) return emptyPassFormValues;

  return {
    name: pass.name,
    startDate: formatDateForInput(pass.startsAt),
    endDate: pass.endsAt ? formatDateForInput(pass.endsAt) : '',
    remainingCredits: pass.credits?.remainingCredits.toString() ?? '',
    totalCredits: pass.credits?.maxCredits.toString() ?? '',
    isUnlimitedDuration: pass.endsAt === null,
    isUnlimitedCredits: pass.credits === null,
  };
};

export const passFormToDraft = (values: PassFormValues): Omit<Pass, 'id'> => {
  const remainingCredits = parseInt(values.remainingCredits, 10);
  const totalCredits = parseInt(values.totalCredits, 10);

  return {
    name: values.name.trim(),
    startsAt: formatDateForInput(values.startDate),
    endsAt: values.isUnlimitedDuration
      ? null
      : values.endDate
        ? formatDateForInput(values.endDate)
        : null,
    credits: values.isUnlimitedCredits
      ? null
      : {
          remainingCredits,
          maxCredits: totalCredits,
        },
  };
};

export const validatePassEndDate = (
  endDate: string,
  startDate: string,
  isUnlimitedDuration: boolean
): true | string => {
  if (isUnlimitedDuration) return true;
  if (!endDate) return 'End date is required unless unlimited';
  if (startDate && compareCalendarDates(startDate, endDate) > 0) {
    return 'End date cannot be before start date';
  }
  return true;
};

const parseNonNegativeInteger = (value: string): number | null => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;

  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
};

export const validatePassTotalCredits = (
  totalCredits: string,
  isUnlimitedCredits: boolean
): true | string => {
  if (isUnlimitedCredits) return true;
  if (!totalCredits.trim()) return 'Total credits are required unless unlimited';

  const parsed = parseNonNegativeInteger(totalCredits);
  if (parsed === null || parsed <= 0) return 'Total credits must be greater than zero';

  return true;
};

export const validatePassRemainingCredits = (
  remainingCredits: string,
  isUnlimitedCredits: boolean,
  totalCredits: string
): true | string => {
  if (isUnlimitedCredits) return true;
  if (!remainingCredits.trim()) return 'Remaining credits are required unless unlimited';

  const parsedRemaining = parseNonNegativeInteger(remainingCredits);
  if (parsedRemaining === null) return 'Remaining credits must be zero or greater';

  const parsedTotal = parseNonNegativeInteger(totalCredits);
  if (parsedTotal !== null && parsedRemaining > parsedTotal) {
    return 'Remaining credits cannot exceed total credits';
  }

  return true;
};

export const durationDaysBetween = (startDate: string, endDate: string): number | null => {
  if (!startDate || !endDate) return null;
  const start = parseCalendarDateInput(startDate);
  const end = parseCalendarDateInput(endDate);
  if (!start || !end) return null;
  if (compareCalendarDates(start, end) > 0) return null;

  return getCalendarDayDifference(start, end) + 1;
};
